import type { CompositionV2Entity } from '@revelio/data-access';

import type { PrimaryFilterId } from '../../../filters/types/filters';
import { mapPositionsEntitiesToCompositionV3 } from '../../../filters/utils/role-skill-ent-kind';
import { useChart } from '../../Chart/ChartProvider';
import { useTabContentConfig } from '../../PageConfigProvider/PageConfigProvider';
import type { SecondaryDimensionValues } from '../../PageConfigProvider/types/dimension';
import type { DimensionSource } from '../../PageConfigProvider/types/item';
import type { CompositionMetricValues } from '../../PageConfigProvider/types/metric';
import type { DataProviderChildren, DataProviderSet } from '../types';
import { CompositionRawDataProvider } from './CompositionRawDataProvider';
import { CompositionTopCompetitorsDataProvider } from './CompositionTopCompetitorsDataProvider';
import { CompositionTopNDataProvider } from './CompositionTopNDataProvider';
import { getCompositionQueryType } from './composition-query-type';
import {
  convertCompositionDataV3,
  convertCompositionTopNHeadcountDataV3,
} from './composition-v3/convert-composition-data-v3';

type CompositionDataProviderBaseProps = {
  primaryDimension: PrimaryFilterId;
  dimensionSource?: DimensionSource;
  segment?: SecondaryDimensionValues;
  metric: CompositionMetricValues;
  isTimeseries?: boolean;
  entitiesOverride?: CompositionV2Entity[];
  getSelectionBreakdowns?: boolean;
  exchangeRate?: number;
  children: DataProviderChildren<DataProviderSet | undefined>;
};

export const CompositionDataProvider = ({
  primaryDimension,
  dimensionSource,
  segment,
  metric,
  isTimeseries,
  entitiesOverride,
  getSelectionBreakdowns,
  exchangeRate,
  children,
}: CompositionDataProviderBaseProps) => {
  const { item } = useChart();
  const queryType = getCompositionQueryType({
    chartType: item.plot?.chartType,
    dimensionSource: dimensionSource ?? item.plot?.dimensionSource,
    metric,
  });
  const { revelioPageId } = useTabContentConfig();

  const entitiesV3 = mapPositionsEntitiesToCompositionV3(entitiesOverride);

  if (queryType.type === 'TOP_N') {
    return (
      <CompositionTopNDataProvider
        primaryDimension={primaryDimension}
        secondaryDimension={queryType.segment}
        metric={metric}
        isTimeseries={isTimeseries}
        exchangeRate={exchangeRate}
      >
        {children}
      </CompositionTopNDataProvider>
    );
  }

  if (queryType.type === 'TOP_HEADCOUNT') {
    const headcountSegment = queryType.segment;
    if (headcountSegment === 'competitors') {
      return (
        <CompositionTopCompetitorsDataProvider
          primaryDimension={primaryDimension}
          metric={metric}
          exchangeRate={exchangeRate}
        >
          {children}
        </CompositionTopCompetitorsDataProvider>
      );
    }

    return (
      <CompositionRawDataProvider
        primaryDimension={primaryDimension}
        getSelectionBreakdowns={!revelioPageId}
      >
        {({ data, status, error }) =>
          children({
            data: data?.composition_v3
              ? convertCompositionTopNHeadcountDataV3({
                  segment: headcountSegment,
                  data: data.composition_v3,
                })
              : undefined,
            status,
            error,
          })
        }
      </CompositionRawDataProvider>
    );
  }

  return (
    <CompositionRawDataProvider
      primaryDimension={primaryDimension}
      entitiesOverride={entitiesV3}
      getSelectionBreakdowns={getSelectionBreakdowns}
      isTimeseries={isTimeseries}
      secondaryDimension={item?.plot?.secondaryDimension}
    >
      {({ data, status, error, monthRefs = [] }) =>
        children({
          data: data?.composition_v3
            ? convertCompositionDataV3({
                metric,
                segment:
                  isTimeseries && !item?.plot?.secondaryDimension
                    ? 'timeseries'
                    : item?.plot?.secondaryDimension || segment,
                data: data.composition_v3,
                isTimeseries,
                exchangeRate,
                monthRefs,
              })
            : undefined,
          status,
          error,
        })
      }
    </CompositionRawDataProvider>
  );
};
